import React, { useCallback, useContext, useEffect, useState } from "react";
import i18next from "i18next";
import { Box, Collapse, Typography } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import { useHistory, useLocation } from "react-router-dom";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import TuneIcon from "@mui/icons-material/Tune";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { BRAND, FONT, RADIUS, SHADOW, useLightStyles, MEDIA } from "./lightTheme";
import { Context } from "states/Store";
import { getFilteredContextInstances, saveContextInstance, isContextInstanceNameAvailable, getAccountAttributeById } from "services/edge/ContexteService";
import { getAccountAttributesByBondType } from "services/edge/AttributesService";
import { hasAttributeListItems } from "services/common/AttributeUtils";
import { AttributeType } from "services/common/Constants";
import { Dialog, DialogContent, DialogTitle, IconButton, CircularProgress } from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import LightLoader from "./LightLoader";
import { formatDt } from "./utils/lightUtils";
import { getUrlWithAccountId } from "config/Routes";

// ─── Styles ───────────────────────────────────────────────────────────────────
const useStyles = makeStyles()(() => ({
  tableWrap: {
    width: "100%", backgroundColor: BRAND.white, borderRadius: RADIUS.lg,
    border: `1.5px solid ${BRAND.border}`, boxShadow: SHADOW.soft,
  },
  tableHeader: {
    display: "grid", alignItems: "center",
    padding: "0.75rem 1.25rem", backgroundColor: BRAND.background,
    borderBottom: `1px solid ${BRAND.border}`, gap: "0.5rem",
    position: "sticky", top: "64px", zIndex: 10,
    [MEDIA.smDown]: { display: "none" },
  },
  tableHeaderCell: {
    ...FONT.label, color: BRAND.textMuted, fontFamily: FONT.family,
    lineHeight: 1.3, wordBreak: "break-word",
  },
  tableRow: {
    display: "grid",
    padding: "1rem 1.25rem", alignItems: "center", gap: "0.5rem",
    cursor: "pointer", transition: "background 0.12s",
    borderBottom: `1px solid ${BRAND.border}`,
    "&:last-child": { borderBottom: "none" },
    "&:hover": { backgroundColor: BRAND.background },
    "&:hover $rowAction": { opacity: 1 },
    [MEDIA.smDown]: {
      gridTemplateColumns: "1fr !important",
      gap: "0.5rem",
      padding: "0.85rem 1rem",
    },
  },
  rowAction: {
    display: "flex", alignItems: "center", justifyContent: "flex-end",
    gap: "0.2rem", color: BRAND.primary, fontFamily: FONT.family,
    fontSize: "0.72rem", fontWeight: 600, opacity: 0.4,
    transition: "opacity 0.15s",
  },
  ctxCell: { display: "flex", alignItems: "center", gap: "0.75rem", minWidth: 0 },
  ctxIcon: {
    width: "36px", height: "36px", borderRadius: RADIUS.md, backgroundColor: BRAND.background,
    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
  },
  ctxInfo: { display: "flex", flexDirection: "column", gap: "0.3rem", minWidth: 0 },
  ctxName: {
    fontSize: "0.875rem", fontWeight: 600, color: BRAND.textMain,
    fontFamily: FONT.family, whiteSpace: "nowrap", overflow: "hidden",
    textOverflow: "ellipsis", lineHeight: 1.3,
  },
  ctxSub: { fontSize: "0.72rem", color: BRAND.textMuted, fontFamily: FONT.family, lineHeight: 1.2 },
  cellDate: { fontSize: "0.8rem", color: BRAND.textMuted, fontFamily: FONT.family },
  searchBar: {
    display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "1.25rem",
  },
  searchInput: {
    flex: 1, padding: "0.55rem 1rem", borderRadius: RADIUS.md,
    border: `1.5px solid ${BRAND.border}`, fontFamily: FONT.family,
    fontSize: "0.875rem", color: BRAND.textMain, outline: "none",
    transition: "border-color 0.15s",
    "&:focus": { borderColor: BRAND.primary },
  },
  // Pagination
  pagination: {
    display: "flex", alignItems: "center", justifyContent: "center",
    gap: "0.4rem", padding: "1rem",
    borderTop: `1px solid ${BRAND.border}`,
  },
  pageBtn: {
    minWidth: "32px", height: "32px", borderRadius: RADIUS.sm,
    border: `1px solid ${BRAND.border}`, backgroundColor: BRAND.white,
    fontFamily: FONT.family, fontSize: "0.8rem", fontWeight: 500,
    color: BRAND.textMuted, cursor: "pointer", transition: "all 0.12s",
    display: "flex", alignItems: "center", justifyContent: "center",
    padding: "0 0.5rem",
    "&:hover": { backgroundColor: BRAND.background, borderColor: BRAND.primary, color: BRAND.primary },
    "&:disabled": { opacity: 0.4, cursor: "not-allowed" },
  },
  pageBtnActive: {
    backgroundColor: BRAND.primary, color: BRAND.white,
    borderColor: BRAND.primary,
    "&:hover": { backgroundColor: BRAND.primary, color: BRAND.white, borderColor: BRAND.primary },
  },

  // ── Collapsible filters (mobile) ──
  filterToggle: {
    display: "none",
    [MEDIA.smDown]: {
      display: "flex", alignItems: "center", gap: "0.5rem",
      cursor: "pointer", width: "100%",
      marginBottom: "0.25rem", userSelect: "none",
    },
  },
  filterToggleLabel: {
    ...FONT.body, fontWeight: 700, color: BRAND.textMain, fontFamily: FONT.family,
    display: "flex", alignItems: "center", gap: "0.4rem", flex: 1,
  },
  filterCountBadge: {
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    minWidth: "20px", height: "20px", padding: "0 6px",
    borderRadius: "10px", backgroundColor: BRAND.primary, color: BRAND.white,
    fontSize: "0.68rem", fontWeight: 600, fontFamily: FONT.family,
  },
  filterCollapse: { width: "100%" },
}));

const ROW_PER_PAGE = 20;

// ─── Columns config ───────────────────────────────────────────────────────────
const COLS = "minmax(150px, 2fr) 130px 130px 1fr 80px";

export default function LightContexts() {
  const { classes: s, cx } = useStyles();
  const { classes: light } = useLightStyles();
  const [state, dispatch] = useContext(Context);
  const history = useHistory();
  const location = useLocation();

  // Derive modelId: from query param, or auto-select when only 1 model
  const contextModels = state.contextModels ?? [];
  const queryModelId = new URLSearchParams(location.search).get("modelId");
  const modelId = queryModelId
    ? parseInt(queryModelId, 10)
    : contextModels.length === 1 ? contextModels[0].id : null;

  const [instances, setInstances] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(() => typeof window === "undefined" || window.innerWidth >= 600);
  const [pendingSearch, setPendingSearch] = useState("");
  const [viewArchived, setViewArchived] = useState(false);

  // Add context dialog
  const [addOpen, setAddOpen] = useState(false);
  const [addModelId, setAddModelId] = useState(modelId ?? contextModels[0]?.id ?? "");
  const [addRef, setAddRef] = useState("");
  const [addDesc, setAddDesc] = useState("");
  const [addExpDate, setAddExpDate] = useState("");
  const [addRefError, setAddRefError] = useState("");
  const [addRefOk, setAddRefOk] = useState(false);
  const [checkingRef, setCheckingRef] = useState(false);
  const [addSaving, setAddSaving] = useState(false);
  const [ctxAttrDefs, setCtxAttrDefs] = useState([]);
  const [ctxAttrValues, setCtxAttrValues] = useState({});
  const [ctxAttrListItems, setCtxAttrListItems] = useState({});

  useEffect(() => {
    if (!state.account?.id) return;
    getAccountAttributesByBondType(state.account.id, "context", null)
      .then((r) => {
        const defs = r?.content ?? [];
        setCtxAttrDefs(defs);
        defs.filter((d) => hasAttributeListItems(d.type)).forEach((d) => {
          getAccountAttributeById(state.account.id, d.id)
            .then((res) => {
              const items = res.content?.values?.map((x) => x.value)?.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase())) ?? [];
              setCtxAttrListItems((prev) => ({ ...prev, [d.id]: items }));
            })
            .catch(() => {});
        });
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.account?.id]);

  const handleCtxAttrChange = (label, values) => {
    setCtxAttrValues((prev) => ({ ...prev, [label]: values }));
  };

  const handleAddRefChange = async (ref) => {
    setAddRef(ref);
    setAddRefError("");
    setAddRefOk(false);
    if (!ref.trim() || !addModelId) return;
    setCheckingRef(true);
    try {
      const available = await isContextInstanceNameAvailable(state.account.id, ref.trim(), addModelId, dispatch);
      if (!available) setAddRefError(i18next.t("light.contexts.refNotAvailable"));
      else setAddRefOk(true);
    } catch (_) { setAddRefOk(true); }
    finally { setCheckingRef(false); }
  };

  const handleAddContext = async () => {
    if (!addRef.trim() || !addModelId || !addRefOk) return;
    setAddSaving(true);
    try {
      const modelObj = contextModels.find((m) => String(m.id) === String(addModelId));
      await saveContextInstance(state.account.id, {
        contextModelName: modelObj?.name ?? "",
        reference: addRef.trim(),
        description: addDesc.trim(),
        expirationDate: addExpDate || null,
        contextAttributes: ctxAttrDefs.map((d) => ({
          attributeId: d.id,
          label: d.label,
          values: ctxAttrValues[d.label] ?? [],
          type: d.type,
        })).filter((a) => a.values.length > 0 && a.values[0] !== ""),
      }, dispatch);
      setAddOpen(false);
      setAddRef(""); setAddDesc(""); setAddExpDate(""); setAddRefOk(false); setCtxAttrValues({});
      load(0, search, modelId, viewArchived);
    } catch (_) {}
    finally { setAddSaving(false); }
  };

  const totalPages = Math.max(1, Math.ceil(totalElements / ROW_PER_PAGE));

  const load = useCallback(async (p = 0, ref = search, mid = modelId, archived = viewArchived) => {
    if (!state.account?.id) return;
    setLoading(true);
    try {
      const body = {};
      if (mid) body.modelId = mid;
      if (ref) body.reference = ref;
      const res = await getFilteredContextInstances(
        state.account.id, !archived, ROW_PER_PAGE, p * ROW_PER_PAGE, body, dispatch,
      );
      setInstances(res?.content ?? []);
      setTotalElements(res?.totalElements ?? 0);
    } catch (_) {
      setInstances([]);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.account?.id, modelId, search, viewArchived]);

  useEffect(() => { setPage(0); load(0, search, modelId, viewArchived); }, [state.account?.id, modelId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggleArchived = (archived) => {
    setViewArchived(archived);
    setPage(0);
    setPendingSearch("");
    setSearch("");
    load(0, "", modelId, archived);
  };

  const handleSearch = (e) => {
    e?.preventDefault();
    setSearch(pendingSearch);
    setPage(0);
    load(0, pendingSearch, modelId, viewArchived);
  };

  const handlePageChange = (p) => {
    setPage(p);
    load(p, search, modelId, viewArchived);
  };

  // ── Model label (for subtitle) ──
  const modelLabel = modelId
    ? (state.contextModels ?? []).find((m) => m.id === modelId)?.name ?? ""
    : "";

  return (
    <Box className={light.pageRoot}>
      {/* Banner */}
      <Box className={light.pageHeader} style={viewArchived ? { backgroundColor: "#1D2939", backgroundImage: "none" } : undefined}>
        <Box className={light.pageHeaderInner} sx={{ display: "flex", alignItems: { xs: "flex-start", sm: "center" }, justifyContent: "space-between", flexDirection: { xs: "column", sm: "row" }, gap: { xs: "0.75rem", sm: 0 } }}>
          <Box>
            <Box style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
              <Typography className={light.pageTitleWhite} sx={{ fontSize: { xs: "1.25rem !important", sm: undefined } }}>
                {modelLabel || i18next.t("light.contexts.title")}
              </Typography>
              {viewArchived && (
                <Box style={{ display: "inline-flex", alignItems: "center", gap: "0.3rem", backgroundColor: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: "20px", padding: "0.2rem 0.75rem", fontSize: "0.72rem", fontWeight: 700, color: "#FCA5A5", fontFamily: FONT.family }}>
                  {i18next.t("light.thirdparties.archived")}
                </Box>
              )}
            </Box>
            <Typography className={light.pageSubtitleWhite}>
              {loading ? "" : i18next.t("light.contexts.count", { count: totalElements })}
            </Typography>
          </Box>
          {!viewArchived && (
            <button
              className={light.btnPrimary}
              onClick={() => { setAddModelId(modelId ?? contextModels[0]?.id ?? ""); setAddRef(""); setAddDesc(""); setAddExpDate(""); setAddRefError(""); setAddRefOk(false); setAddOpen(true); }}
              style={{ display: "flex", alignItems: "center", gap: "0.4rem", backgroundColor: BRAND.secondary, color: BRAND.primary, whiteSpace: "nowrap", flexShrink: 0 }}
            >
              <AddCircleOutlineIcon style={{ fontSize: "16px" }} />
              {i18next.t("light.common.add")}
            </button>
          )}
        </Box>
      </Box>

      <Box className={light.pageInner} style={{ marginTop: "-0.75rem" }}>
        {/* Toggle Actifs / Archivés */}
        <Box style={{ marginBottom: "1rem" }}>
          <Box className={light.toggleGroup}>
            <button className={cx(light.toggleBtn, !viewArchived && light.toggleBtnActive)} onClick={() => handleToggleArchived(false)}>
              {i18next.t("light.thirdparties.active")}
            </button>
            <button className={cx(light.toggleBtn, viewArchived && light.toggleBtnActive)} onClick={() => handleToggleArchived(true)}>
              {i18next.t("light.thirdparties.archived")}
            </button>
          </Box>
        </Box>

        {/* Filter card */}
        <Box className={light.filterCard}>
          <Box className={s.filterToggle} onClick={() => setFiltersOpen((o) => !o)}>
            <Typography className={s.filterToggleLabel}>
              <TuneIcon style={{ fontSize: "17px", color: BRAND.primary }} />
              {i18next.t("light.common.filters") || "Filtres"}
              {search ? <span className={s.filterCountBadge}>1</span> : null}
            </Typography>
            <ExpandMoreIcon style={{ color: BRAND.textMuted, transform: filtersOpen ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />
          </Box>
          <Collapse in={filtersOpen} className={s.filterCollapse}>
          <Box className={light.filterRow}>
            <Box className={light.filterField}>
              <Typography className={light.fieldLabel}>{i18next.t("light.contexts.colReference")}</Typography>
              <input
                className={light.fieldInput}
                placeholder={i18next.t("light.contexts.searchPlaceholder")}
                value={pendingSearch}
                onChange={(e) => setPendingSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch(e)}
              />
            </Box>
            <Box className={light.filterActions}>
              <button className={cx(light.btnSecondary, light.btnSmall)} onClick={() => { setPendingSearch(""); setSearch(""); setPage(0); load(0, "", modelId); }}>
                {i18next.t("light.contexts.reset")}
              </button>
              <button className={cx(light.btnPrimary, light.btnSmall)} onClick={handleSearch}>
                {i18next.t("light.contexts.search")}
              </button>
            </Box>
          </Box>
          </Collapse>
        </Box>

        {/* Table */}
        {loading ? (
          <LightLoader />
        ) : instances.length === 0 ? (
          <Box className={cx(light.card, light.emptySection)}>
            <AccountTreeOutlinedIcon style={{ fontSize: "40px", color: BRAND.border, marginBottom: "0.75rem" }} />
            <Typography style={{ fontWeight: 600, color: BRAND.textMain, fontFamily: FONT.family, marginBottom: "0.25rem" }}>
              {i18next.t("light.contexts.empty")}
            </Typography>
          </Box>
        ) : (
          <Box className={s.tableWrap}>
            {/* Header */}
            <Box className={s.tableHeader} style={{ gridTemplateColumns: COLS }}>
              <Typography className={s.tableHeaderCell}>{i18next.t("light.contexts.colReference")}</Typography>
              <Typography className={s.tableHeaderCell}>{i18next.t("light.contexts.colExpiration")}</Typography>
              <Typography className={s.tableHeaderCell}>{i18next.t("light.contexts.colCreation")}</Typography>
              <Typography className={s.tableHeaderCell}>{i18next.t("light.contexts.colCreatedBy")}</Typography>
              <Box />
            </Box>

            {/* Rows */}
            {instances.map((inst) => {
              const createdBy = inst.createdBy
                ? (typeof inst.createdBy === "object"
                    ? `${inst.createdBy.prenom || ""} ${inst.createdBy.nom || ""}`.trim() || inst.createdBy.email || "—"
                    : inst.createdBy)
                : "—";
              return (
                <Box
                  key={inst.id}
                  component="a"
                  href={getUrlWithAccountId(state.account.id, `light/contexts/${inst.id}`)}
                  className={s.tableRow}
                  style={{ gridTemplateColumns: COLS, textDecoration: "none", color: "inherit" }}
                  onClick={(e) => { if (e.metaKey || e.ctrlKey || e.button === 1) return; e.preventDefault(); history.push(getUrlWithAccountId(state.account.id, `light/contexts/${inst.id}`)); }}
                >
                  {/* Reference + model name */}
                  <Box className={s.ctxCell}>
                    <Box className={s.ctxIcon}>
                      <AccountTreeOutlinedIcon style={{ fontSize: "18px", color: BRAND.primary }} />
                    </Box>
                    <Box className={s.ctxInfo}>
                      <Typography className={s.ctxName}>{inst.reference || `#${inst.id}`}</Typography>
                    </Box>
                  </Box>

                  {/* Expiration */}
                  <Typography className={s.cellDate}>
                    {inst.expirationDate ? formatDt(inst.expirationDate) : "—"}
                  </Typography>

                  {/* Creation */}
                  <Typography className={s.cellDate}>
                    {inst.creationDate ? formatDt(inst.creationDate) : "—"}
                  </Typography>

                  {/* Created by */}
                  <Typography className={s.cellDate} style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {createdBy}
                  </Typography>

                  {/* Arrow */}
                  <Box className={s.rowAction}>
                    {i18next.t("light.common.see")} <ChevronRightIcon style={{ fontSize: "16px" }} />
                  </Box>
                </Box>
              );
            })}

            {/* Pagination */}
            {totalPages > 1 && (
              <Box className={s.pagination}>
                <button className={s.pageBtn} disabled={page === 0} onClick={() => handlePageChange(page - 1)}>‹</button>
                {Array.from({ length: totalPages }, (_, i) => i)
                  .filter((i) => Math.abs(i - page) <= 2 || i === 0 || i === totalPages - 1)
                  .reduce((acc, i, idx, arr) => {
                    if (idx > 0 && i - arr[idx - 1] > 1) acc.push("…");
                    acc.push(i);
                    return acc;
                  }, [])
                  .map((item, idx) =>
                    item === "…" ? (
                      <span key={`ellipsis-${idx}`} style={{ padding: "0 0.25rem", color: BRAND.textMuted, fontSize: "0.8rem" }}>…</span>
                    ) : (
                      <button key={item} className={cx(s.pageBtn, item === page && s.pageBtnActive)} onClick={() => handlePageChange(item)}>
                        {item + 1}
                      </button>
                    )
                  )}
                <button className={s.pageBtn} disabled={page >= totalPages - 1} onClick={() => handlePageChange(page + 1)}>›</button>
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* ── Dialog ajout entrée ── */}
      <Dialog
        open={addOpen}
        onClose={() => { if (!addSaving) setAddOpen(false); }}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: RADIUS.lg, fontFamily: FONT.family, overflow: "hidden" } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontFamily: "Inter", fontSize: "1rem", fontWeight: 700, color: BRAND.white, backgroundColor: BRAND.primary, pb: 1.5 }}>
          <Box style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <AddCircleOutlineIcon style={{ fontSize: "18px" }} />
            {i18next.t("light.contexts.addEntry")}
          </Box>
          <IconButton size="small" onClick={() => { if (!addSaving) setAddOpen(false); }} sx={{ color: "rgba(255,255,255,0.7)", "&:hover": { color: BRAND.white } }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2, pb: 3 }}>
          <Box style={{ display: "flex", flexDirection: "column", gap: "1rem", marginTop: "0.5rem" }}>
            {/* Model selector (if multiple) */}
            {contextModels.length > 1 && (
              <Box>
                <Typography className={light.fieldLabel}>{i18next.t("light.launch.contextModel")}</Typography>
                <select
                  className={light.fieldSelect}
                  value={addModelId}
                  onChange={(e) => { setAddModelId(e.target.value); setAddRef(""); setAddRefOk(false); setAddRefError(""); }}
                  style={{ width: "100%" }}
                >
                  <option value="">{i18next.t("light.launch.selectModel")}</option>
                  {contextModels.map((m) => (
                    <option key={m.id} value={m.id}>{m.name}</option>
                  ))}
                </select>
              </Box>
            )}

            {/* Reference */}
            <Box>
              <Typography className={light.fieldLabel}>{i18next.t("light.contexts.colReference")}</Typography>
              <Box style={{ position: "relative", display: "flex", alignItems: "center" }}>
                <input
                  className={light.fieldInput}
                  style={{ width: "100%", paddingRight: "2rem" }}
                  placeholder={i18next.t("light.launch.contextRefPlaceholder")}
                  value={addRef}
                  onChange={(e) => handleAddRefChange(e.target.value)}
                  disabled={!addModelId}
                />
                {checkingRef && <CircularProgress size={14} style={{ color: BRAND.primary, position: "absolute", right: "0.65rem" }} />}
                {!checkingRef && addRefOk && <CheckCircleOutlinedIcon style={{ fontSize: "16px", color: "#16a34a", position: "absolute", right: "0.65rem" }} />}
              </Box>
              {addRefError && <Typography style={{ ...FONT.small, color: "#DC2626", fontFamily: FONT.family, marginTop: "0.25rem" }}>{addRefError}</Typography>}
            </Box>

            {/* Description */}
            <Box>
              <Typography className={light.fieldLabel}>{i18next.t("light.contexts.description")}</Typography>
              <textarea
                className={light.fieldInput}
                style={{ width: "100%", boxSizing: "border-box", resize: "vertical", lineHeight: 1.5, minHeight: "80px" }}
                placeholder={i18next.t("light.contexts.description") + "…"}
                value={addDesc}
                onChange={(e) => setAddDesc(e.target.value)}
                rows={2}
              />
            </Box>

            {/* Context attributes */}
            {ctxAttrDefs.length > 0 && (
              <Box style={{ borderTop: `1px solid ${BRAND.border}`, paddingTop: "0.75rem" }}>
                <Typography className={light.fieldLabel} style={{ marginBottom: "0.5rem" }}>{i18next.t("light.detail.attributes")}</Typography>
                <Box style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                  {ctxAttrDefs.map((def) => {
                    const val = ctxAttrValues[def.label] ?? [];
                    const items = ctxAttrListItems[def.id] ?? [];
                    const isList = def.type === AttributeType.LIST_UNIQUE;
                    const isMulti = def.type === AttributeType.LIST_MULTIPLE;
                    const isBool = def.type === AttributeType.BOOLEAN;
                    const isDate = def.type === AttributeType.DATE;
                    return (
                      <Box key={def.id}>
                        <Typography style={{ ...FONT.label, fontSize: "0.68rem", color: BRAND.textMuted, fontFamily: FONT.family, marginBottom: "0.25rem" }}>
                          {def.label} {def.isRequired && <span style={{ color: BRAND.primary }}>*</span>}
                        </Typography>
                        {(isList || isBool) ? (
                          <select
                            className={light.fieldSelect}
                            style={{ width: "100%" }}
                            value={val[0] ?? ""}
                            onChange={(e) => handleCtxAttrChange(def.label, e.target.value ? [e.target.value] : [])}
                          >
                            <option value="">—</option>
                            {isBool
                              ? [<option key="true" value="true">{i18next.t("yes")}</option>, <option key="false" value="false">{i18next.t("no")}</option>]
                              : items.map((it) => <option key={it} value={it}>{it}</option>)
                            }
                          </select>
                        ) : isMulti ? (
                          <Box style={{ display: "flex", flexDirection: "column", gap: "0.2rem", maxHeight: "100px", overflowY: "auto", padding: "0.4rem 0.5rem", border: `1.5px solid ${BRAND.border}`, borderRadius: RADIUS.sm, backgroundColor: BRAND.white }}>
                            {items.length === 0 ? <span style={{ ...FONT.small, color: BRAND.textMuted, fontStyle: "italic" }}>—</span> : items.map((it) => (
                              <label key={it} style={{ display: "flex", alignItems: "center", gap: "0.4rem", cursor: "pointer", ...FONT.small, fontFamily: FONT.family }}>
                                <input type="checkbox" checked={val.includes(it)} onChange={(e) => { const next = e.target.checked ? [...val, it] : val.filter((v) => v !== it); handleCtxAttrChange(def.label, next); }} style={{ accentColor: BRAND.primary }} />
                                {it}
                              </label>
                            ))}
                          </Box>
                        ) : (
                          <input
                            type={isDate ? "date" : "text"}
                            className={light.fieldInput}
                            style={{ width: "100%", boxSizing: "border-box" }}
                            value={val[0] ?? ""}
                            onChange={(e) => handleCtxAttrChange(def.label, e.target.value ? [e.target.value] : [])}
                            placeholder="—"
                          />
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}

            {/* Expiration date */}
            <Box>
              <Typography className={light.fieldLabel}>{i18next.t("light.contexts.colExpiration")}</Typography>
              <input
                type="date"
                className={light.fieldInput}
                style={{ width: "100%", boxSizing: "border-box" }}
                value={addExpDate}
                onChange={(e) => setAddExpDate(e.target.value)}
              />
            </Box>

            {/* Actions */}
            <Box style={{ display: "flex", justifyContent: "flex-end", gap: "0.75rem", marginTop: "0.5rem" }}>
              <button className={light.btnSecondary} style={{ padding: "0.5rem 1.25rem" }} onClick={() => setAddOpen(false)} disabled={addSaving}>
                {i18next.t("light.common.cancel")}
              </button>
              <button className={light.btnPrimary} style={{ padding: "0.5rem 1.25rem" }} onClick={handleAddContext} disabled={!addRefOk || addSaving}>
                {addSaving ? i18next.t("light.common.loading") : i18next.t("light.common.add")}
              </button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
